// Browser-side Web Push helpers.
//
// Flow: ensureSubscribed(code) reads the current permission, prompts if
// 'default', registers a PushSubscription against the runtime SW (vite-plugin-pwa
// installs one in both dev and prod), and POSTs it to /api/push/subscribe.

const VAPID_PUBLIC_KEY: string = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '';

export type PushState = 'unsupported' | 'denied' | 'granted' | 'default' | 'no-vapid';

export function getInitialPushState(): PushState {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (!VAPID_PUBLIC_KEY) return 'no-vapid';
  return (Notification.permission as PushState) ?? 'default';
}

export async function ensureSubscribed(code: string): Promise<PushState> {
  if (getInitialPushState() === 'unsupported') return 'unsupported';
  if (!VAPID_PUBLIC_KEY) return 'no-vapid';

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return permission as PushState;

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  const raw = sub.toJSON();
  if (!raw.endpoint || !raw.keys?.p256dh || !raw.keys?.auth) return 'denied';

  const res = await fetch('/api/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      code,
      subscription: {
        endpoint: raw.endpoint,
        keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
      },
    }),
  });
  if (!res.ok) return 'denied';
  return 'granted';
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const standard = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(standard);
  // Allocate the underlying ArrayBuffer explicitly so the resulting view's
  // buffer is firmly ArrayBuffer (not ArrayBufferLike) — required by PushManager.
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
